



















































































































































































































































import { Base } from '@master/model/Base'
import { FormStructure, Input, TableSetup } from '@master/model/structure/FormStructure'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import AppCheckboxGroup from '@core/components/AppCheckboxGroup.vue'
import AppFormTable from '@core/components/AppFormTable.vue'
import AppRadioSelection from '@core/components/AppRadioSelection.vue'
import AppSmartSelect from '@core/components/AppSmartSelect.vue'
import { inject } from 'inversify-props'
import { IDateService } from '@master/services/IDateService'
import AppDateInput from './AppDateInput.vue'
import { getMaxChar } from '@master/annotation/MaxChar'
import { IProjectSettings } from '@master/config/IProjectSettings'
import { isUndefined } from 'lodash/fp'
import randomstring from 'randomstring'
import AppCurrencyNA from './AppCurrencyNA.vue'
import AppDecimalNumber from './AppDecimalNumber.vue'
import AppTooltip from './AppTooltip.vue'

@Component({
  components: {
    AppSmartSelect,
    AppCheckboxGroup,
    AppRadioSelection,
    AppDateInput,
    AppFormTable,
    AppCurrencyNA,
    AppDecimalNumber,
    AppTooltip
  }
})

export default class AppForm<T extends Base> extends Vue {
  @Prop() readonly structures!: FormStructure<T>[];
  @Prop() readonly model!: T;
  @Prop({ default: 1 }) readonly columns: number;
  @Prop() readonly errors?: { key: string; msg: string }[];
  @Prop({ default: () => { return Date.now() } })
  readonly uniqueID: string;

  @Prop({ default: false }) readonly readOnly: boolean;

  @inject()
  private dateService: IDateService;

  @inject()
  private projectSettings: IProjectSettings;

  private minimal = true;
  // private renderCnt = 0;
  private focusId = null;
  private fullErr = null;

  get structuresDetails () {
    return this.structures.map((structure) => {
      const key = (structure as any).key
      const errors = this.errors?.filter((err) => err.key === key) || []
      const maxChar = getMaxChar(this.model, key)
      const charLeft = this.getCharLeft(key)
      const counterMsg = this.getCounterMsg(maxChar, charLeft)
      return {
        errors,
        hasErrors: errors?.length > 0,
        maxChar,
        counterMsg,
        ...structure
      }
    })
  }

  get gridStyle () {
    return {
      gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`
    }
  }

  get isModernTheme () {
    return this.projectSettings.theme === 'Modern'
  }

  getCounterMsg (maxChar, charLeft) {
    if (!this.isModernTheme) return '' // counterMsg only used in Modern Theme
    return maxChar ? charLeft > 0 ? `${charLeft} letters remaining` : 'Max character limit reached' : ''
  }

  shouldDisabled (condition: boolean | Function = false) {
    if (this.readOnly) return true
    if (condition === undefined) {
      return false
    } else if (condition instanceof Function) {
      return condition(this.model)
    } else {
      return condition
    }
  }

  shouldShow (condition: boolean | Function = true) {
    if (condition === undefined) {
      return false
    } else if (condition instanceof Function) {
      return condition(this.model)
    } else {
      return condition
    }
  }

  getValue (input: Input<Base>) {
    if (input.type === 'currency') {
      const oriValue = this.model[input.key]
      if (typeof oriValue === 'string' || oriValue instanceof String) {
        return Number(this.model[input.key])
      }
    }
    if (input.key) {
      return this.model[input.key]
    } else if (input.model) {
      return input.model.get()
    } else {
      throw new Error(
        `Invalid config for label ${input.label}: key or model is required`
      )
    }
  }

  /**
   * Return null as the label if it is empty so
   * that the placeholder will appear
   */
  labelModelValue (labelValue: string) {
    return !labelValue ? null : labelValue
  }

  getValueForTable (input: TableSetup<Base>) {
    if (input.key) {
      return this.model[input.key]
    } else if (input.data) {
      return input.data
    } else {
      throw new Error(
        `Invalid config for label ${input.label}: key or model is required`
      )
    }
  }

  // this is to resolve the issue of event.target.value is always passed as string, but our use cases might need number
  setSelectValue (event, structure) {
    const selectedOption = structure.options[event.selectedIndex]
    this.setValue(structure, selectedOption.value)
  }

  setValue (input: Input<T>, _value: T[keyof T]) {
    const value: any = input.type === 'number' ? +_value || 0 : _value

    if (input.key) {
      if (input.onChange) {
        input.onChange(this.model[input.key], value)
      }
      // when only has getter (for currency input), sidestep the error
      try {
        this.model[input.key] = value
      } catch (ex) {}
    } else if (input.model) {
      if (input.onChange) {
        input.onChange(this.model[input.key], value)
      }
      return input.model.set(value)
    } else {
      throw new Error(`Invalid config for label ${input.label}: key or model is required`)
    }
  }

  getId (key: string) {
    if (this.model && 'snakeCaseKey' in this.model) {
      return `${this.uniqueID}_${this.model.snakeCaseKey(key)}`
    }

    if (isUndefined(key)) {
      key = randomstring.generate(6)
    }

    if (this.uniqueID) {
      return `${this.uniqueID}_${key}`
    }

    return `${key}`
  }

  getTestId (key: string) {
    if (this.model && 'snakeCaseKey' in this.model) {
      return `${this.model.snakeCaseKey(key)}`
    }
    return undefined
  }

  getLabelId (key: string) {
    return this.getId(key) + '_label'
  }

  arrangeCols () {
    const curAppForm = this.$el
    const hasOverflow = curAppForm.scrollWidth > curAppForm.clientWidth
    if (hasOverflow) this.minimal = true
  }

  blurHandler (input: Input<T>, _value: T[keyof T]) {
    if (this.isModernTheme) {
      this.focusId = null
    }

    if (input && input?.onBlur) {
      input.onBlur(_value)
    }
  }

  // use to handle Modern theme
  focusInner (structure: any) {
    // this means label is also editable, thus do not auto focus input
    if (structure.labelModel || structure.labelKey || structure.model) return
    const eleId = this.getId(structure.key)
    this.focusId = eleId
    const ele = document.getElementById(eleId)
    if (ele) ele.focus()
  }

  getCharLeft (key: string) {
    const mc = getMaxChar(this.model, key)
    return mc ? mc - (this.model[key]?.length || 0) : mc
  }

  getCustomClass (structure) {
    return structure.classes ? [...structure.classes] : []
  }

  // @Watch('structures')
  // structuresUpdated () {
  //   // this is to allow view to be updated when structures change
  //   this.renderCnt++
  // }

  @Watch('structuresDetails', { deep: true })
  focusError (swe) {
    if (!this.isModernTheme) return // this is used for Modern Theme

    const first = swe.find((s) => 'msg' in s)
    if (!first) return
    const ele = document.getElementById(`box-${this.getId(first.key)}`)
    if (!ele) return
    ele.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  updated () {
    this.arrangeCols()
  }

  mounted () {
    this.arrangeCols()
    window.addEventListener('resize', () => this.arrangeCols())
  }
}
